import React, { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { StatusViewsContext } from '../StatusViews.context';
import { CenteredContainer, Description, Title, Wrapper } from '../StatusViews.styles';
import { RequiredDocsDTO, useRequiredDocsQuery } from '../api/useRequiredDocsQuery';
import { Loading } from '../../../CommonComponents/OPL/Loading/Loading';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { ErrorState } from '../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { Content, StickyFooter } from '../../../CommonComponents/OPL/StickyFooter/StickyFooter';
import { useResetModuleWrapperStyles } from '../../../_hooks/useResetModuleWrapperStyles';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { useSendAppealRequestMutation } from '../api/useSendAppealRequestMutation';
import { APEX_ACCOUNT_STATUS_VIEW } from '../StatusViews.meta';
import { setAccountApexStatus } from '../../../ContextualSidebar/NetWorthSidebar/NetWorthSidebarActions';
import { ApplicantOptions } from './components/ApplicantOptions/ApplicantOptions';
import {
  AppealContextProvider,
  AppealContextValue,
  UploadedDocumentsState,
} from './context/AppealContext';

interface Props {
  setStatusView: (view: string) => void;
}

export const Appeal = ({ setStatusView }: Props) => {
  useResetModuleWrapperStyles();

  const { clientId, accountId } = useContext(StatusViewsContext);

  const { isLoading, isError, refetch, data } = useRequiredDocsQuery({ clientId, accountId });

  if (isLoading) {
    return (
      <Wrapper>
        <CenteredContainer>
          <Loading text="Loading..." />
        </CenteredContainer>
      </Wrapper>
    );
  }

  if (isError) {
    return (
      <Wrapper>
        <CenteredContainer>
          <ErrorState onAction={refetch} />
        </CenteredContainer>
      </Wrapper>
    );
  }

  if (!data) {
    throw new Error('No data for required documents.');
  }

  return <AppealContent data={data} setStatusView={setStatusView} />;
};

interface ContentProps {
  data: RequiredDocsDTO;
  setStatusView: (view: string) => void;
}

const AppealContent = ({ data, setStatusView }: ContentProps) => {
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocumentsState>({
    primaryApplicant: [],
    secondaryApplicant: [],
  });

  const [submitError, setSubmitError] = useState<string | null>(null);

  const { clientId, accountId } = useContext(StatusViewsContext);

  const {
    mutateAsync: sendAppealRequest,
    isLoading: isSendingAppealRequest,
    error: sendAppealRequestError,
    reset: resetSendAppealRequest,
  } = useSendAppealRequestMutation();

  const dispatch = useDispatch();

  const addUploadedDocument: AppealContextValue['addUploadedDocument'] = (
    document,
    isSecondaryApplicant = false,
  ) => {
    const key = isSecondaryApplicant ? 'secondaryApplicant' : 'primaryApplicant';

    const documentExists = uploadedDocuments[key].find(
      ({ documentType }) => documentType === document.documentType,
    );

    if (!documentExists) {
      setUploadedDocuments({
        ...uploadedDocuments,
        [key]: [...uploadedDocuments[key], document],
      });
    }
  };

  const removeUploadedDocument: AppealContextValue['removeUploadedDocument'] = (
    documentType,
    isSecondaryApplicant = false,
  ) => {
    const key = isSecondaryApplicant ? 'secondaryApplicant' : 'primaryApplicant';

    setUploadedDocuments({
      ...uploadedDocuments,
      [key]: uploadedDocuments[key].filter(doc => doc.documentType !== documentType),
    });
  };

  const { primaryApplicantOptions, secondaryApplicantOptions } = data;

  const handleSubmit = async () => {
    setSubmitError(null);

    let isIncomplete = false;

    [primaryApplicantOptions, secondaryApplicantOptions].forEach((applicantOptions, index) => {
      if (applicantOptions) {
        const { acceptableDocumentsByCategory } = applicantOptions;
        const applicantDocuments =
          uploadedDocuments[index === 0 ? 'primaryApplicant' : 'secondaryApplicant'];

        if (!applicantDocuments) {
          isIncomplete = true;
          return;
        }

        const areAllCategoriesVerified = acceptableDocumentsByCategory.every(
          ({ acceptableDocuments }) =>
            acceptableDocuments.some(({ documentType }) =>
              applicantDocuments.some(({ documentType: docType }) => docType === documentType),
            ),
        );

        if (!areAllCategoriesVerified) {
          isIncomplete = true;
        }
      }
    });

    if (isIncomplete) {
      setSubmitError('Please check that all documents required are uploaded.');
      return;
    }

    const snapIds = Object.values(uploadedDocuments)
      .map(docs => docs.map(({ snapId }) => snapId))
      .flat();

    try {
      await sendAppealRequest({ clientId, accountId, snapIds });

      dispatch(setAccountApexStatus(accountId, 'Pending'));
      setStatusView(APEX_ACCOUNT_STATUS_VIEW.PENDING_VIEW);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <AppealContextProvider
      value={{
        uploadedDocuments,
        addUploadedDocument,
        removeUploadedDocument,
      }}
    >
      <Box pt={56}>
        <Content display="flex" flexDirection="column" justifyContent="center">
          <Wrapper>
            <CenteredContainer mx="32">
              <Title>New Account Application Needs More Information</Title>

              <Description>
                There was a problem opening your account. Please provide the requested information
                below to resolve this.
              </Description>

              <Box width="100%" maxWidth={550} mx="auto">
                {submitError && (
                  <Alert
                    message={submitError}
                    onRequestClose={() => setSubmitError(null)}
                    variant="error"
                    mb="16"
                  />
                )}

                {sendAppealRequestError && (
                  <Alert
                    message={sendAppealRequestError.message}
                    onRequestClose={resetSendAppealRequest}
                    variant="error"
                    mb="16"
                  />
                )}

                {primaryApplicantOptions &&
                  primaryApplicantOptions.acceptableDocumentsByCategory.length > 0 && (
                    <ApplicantOptions applicantOptions={primaryApplicantOptions} />
                  )}

                {secondaryApplicantOptions &&
                  secondaryApplicantOptions.acceptableDocumentsByCategory.length > 0 && (
                    <ApplicantOptions
                      isSecondaryApplicant
                      applicantOptions={secondaryApplicantOptions}
                    />
                  )}
              </Box>
            </CenteredContainer>
          </Wrapper>
        </Content>

        <StickyFooter fullWidthChildren>
          <Button
            size="large"
            isLoading={isSendingAppealRequest}
            onClick={handleSubmit}
            ml="auto"
            mr="32"
          >
            Send
          </Button>
        </StickyFooter>
      </Box>
    </AppealContextProvider>
  );
};
